<template>
  <q-dialog
    ref="dialog"
    @hide="onDialogHide"
  >
    <q-card
      flat
      bordered
      class="q-dialog-plugin"
      style="max-width: 800px; width: 90vw; white-space: pre-wrap"
    >
      <q-toolbar
        dense
        color="primary"
      >
        <q-avatar>
          <q-icon
            name="mdi-arrow-collapse"
            color="green"
            size="md"
            class="q-pa-none"
          />
        </q-avatar>

        <q-toolbar-title>
          <span class="text-weight-bold">Selecionar Broker e Template</span>
        </q-toolbar-title>

        <q-btn
          flat
          round
          dense
          icon="mdi-close"
          data-testid="template-selecao-fechar"
          v-close-popup
        />
      </q-toolbar>

      <q-separator inset />

      <q-card-section>
        <div class="row">
          <div class="col column">
            <FieldSelect
              v-if="!preBotId"
              data-testid="template-broker"
              label="Broker"
              :state="bot.state"
              :configs="bot.configs"
              type="text"
              is-required
              v-model="bot.value"
              @input="handleBotChange"
            />

            <FieldSelect
              v-if="!preTemplateId || bot.value"
              data-testid="template-modelo"
              label="Template"
              :state="template.state"
              :configs="template.configs"
              :disabled="!bot.value"
              type="text"
              is-required
              v-model="template.value"
            />
          </div>

          <div
            v-if="selectedTemplate"
            class="col-5 q-pl-xs row"
            style="border-left: 1px solid #ddd"
          >
            <div class="column">
              <div
                class="col-auto text-bold q-mb-sm"
                v-if="previewHeader"
              >
                <q-img
                  v-if="isImage"
                  :src="previewHeader"
                  loading="lazy"
                  spinner-color="#ddd"
                  width="80px"
                  height="80px"
                />

                <iframe
                  v-else-if="isDocument"
                  :src="previewHeader"
                  width="95%"
                />

                <div
                  v-else-if="isVideo"
                  class="row items-center"
                >
                  <video
                    :src="previewHeader"
                    width="95%"
                    controls
                  />
                </div>

                <div v-else>
                  {{ previewHeader }}
                </div>
              </div>

              <div
                class="col-auto"
                v-html="previewBody"
              />

              <div
                class="col-auto text-italic q-my-sm"
                v-if="previewFooter"
              >
                {{ previewFooter }}
              </div>

              <div class="col-auto column">
                <template
                  v-for="(btn, index) in previewButtons"
                >
                  <q-btn
                    size="sm"
                    outline
                    color="primary"
                    class="col-auto q-mt-xs"
                    :key="index"
                  >
                    {{ btn }}
                  </q-btn>
                </template>
              </div>
            </div>
          </div>
        </div>
      </q-card-section>

      <q-separator />

      <q-card-actions align="around">
        <q-btn
          flat
          no-caps
          padding="5px 12px"
          color="primary"
          data-testid="template-selecao-cancelar"
          @click="onCancelClick"
        >
          Cancelar
        </q-btn>

        <q-btn
          no-caps
          unelevated
          padding="5px 12px"
          color="green-8"
          data-testid="template-continuar"
          @click="handleConfirm"
          :disable="!bot.value || !template.value"
        >
          Continuar
        </q-btn>
      </q-card-actions>
    </q-card>
  </q-dialog>
</template>

<script>
import FieldSelect from '@/components/fields/FieldSelect'
import { mapGetters } from 'vuex'
import { sanitizeName } from '@/components/utils/sanitizeName'

export default {
  components: {
    FieldSelect
  },

  props: {
    preBotId: { default: null },
    preTemplateId: { default: null },
    preQueueId: { default: null },
    preFields: { default: null }
  },

  data () {
    const bot = {
      value: this.preBotId,
      state: { dirty: false, error: null },
      configs: { fieldRequired: true, fieldWidth: 'col-auto', values: [] }
    }

    const template = {
      value: null,
      state: { dirty: false, error: null },
      configs: { fieldRequired: true, fieldWidth: 'col-auto', values: [] }
    }

    return { bot, template }
  },

  computed: {
    ...mapGetters({
      allTemplates: 'agentModule/getTemplates'
    }),

    bots () {
      const botsMap = {}
      for (const tmpl of this.allTemplates) {
        const { botId, botName, source } = tmpl
        if (!['ZENVIA', 'WHATSAPP', 'GUPSHUP', 'WHATSAPPAPI'].includes(source)) continue
        if (!botsMap[botId]) {
          botsMap[botId] = { id: botId, label: botName }
        }
      }
      return Object.values(botsMap)
    },

    templates () {
      if (!this.bot.value) return []
      return this.allTemplates
        .filter(t => t.botId === this.bot.value)
        .map(t => ({ id: t.id, label: sanitizeName(t.name), template: t }))
        .sort((a, b) => a.label.localeCompare(b.label))
    },

    selectedTemplate () {
      if (!this.template.value) return null
      return this.allTemplates.find(t => t.id === this.template.value)
    },

    parsedMessage () {
      if (!this.selectedTemplate) return {}
      return tryParse(this.selectedTemplate.message)
    },

    parsedHeader () {
      if (!this.selectedTemplate) return {}
      return tryParse(this.selectedTemplate.header)
    },

    previewBody () {
      if (!this.selectedTemplate) return ''
      return toStr(this.parsedMessage.data) || this.parsedMessage
    },

    previewHeader () {
      return toStr(this.parsedMessage.header || this.parsedHeader?.value)
    },

    previewFooter () {
      return toStr(this.parsedMessage.footer)
    },

    previewButtons () {
      const buttons = Array.isArray(this.parsedMessage.buttons) ? this.parsedMessage.buttons : []
      return buttons.map(({ text }) => text)
    },

    isImage () {
      return this.parsedHeader.type === 'IMAGE'
    },

    isDocument () {
      return this.parsedHeader.type === 'DOCUMENT'
    },

    isVideo () {
      return this.parsedHeader.type === 'VIDEO'
    }
  },

  mounted () {
    this.bot.configs.values = this.bots
    if (this.preBotId) {
      this.handleBotChange()
      if (this.preTemplateId) {
        this.template.value = this.preTemplateId
      }
    }
  },

  methods: {
    show () {
      this.$refs.dialog.show()
    },

    hide () {
      this.$refs.dialog.hide()
    },

    onDialogHide () {
      this.$emit('hide')
    },

    handleBotChange () {
      this.template.value = null
      this.template.configs.values = this.templates

      if (this.preBotId && this.preTemplateId && this.bot.value === this.preBotId) {
        this.template.value = this.preTemplateId
      }
    },

    handleConfirm () {
      if (!this.bot.value) {
        this.bot.state.dirty = true
        this.bot.state.error = 'Campo Obrigatório'
        return
      }

      if (!this.template.value) {
        this.template.state.dirty = true
        this.template.state.error = 'Campo Obrigatório'
        return
      }

      const botChanged = this.preBotId && this.bot.value !== this.preBotId
      const templateChanged = this.preTemplateId && this.template.value !== this.preTemplateId

      this.$emit('ok', {
        botId: this.bot.value,
        templateId: this.template.value,
        template: this.selectedTemplate,
        queueId: (botChanged || templateChanged) ? null : this.preQueueId,
        fields: (botChanged || templateChanged) ? null : this.preFields
      })
      this.hide()
    },

    onCancelClick () {
      this.hide()
    }
  }
}

const toStr = (val) => String(val || '').trim()

const tryParse = (val) => {
  if (!val) return {}

  try {
    return JSON.parse(val)
  } catch {
    return val
  }
}
</script>
